/**
 * Parameter<T> — the backbone of the synth's control plane.
 *
 * Everything that changes sound goes through a Parameter: knob drags, MIDI CC,
 * macro modulation, preset recall, randomize. That's the single design rule
 * that makes all those features cheap to implement later — they're not separate
 * code paths, they're just different `source` tags on `Parameter.set()`.
 *
 * Two flavors:
 *   - NumericParameter — continuous values with range, curve, and optional
 *     AudioParam binding for sample-accurate scheduling.
 *   - EnumParameter<T> — stepped values (waveform, filter type, etc.)
 *
 * Both share a minimal subscribe API. UI components re-render on change,
 * modules push their underlying AudioNodes, presets read `.value`.
 */

export type ParamSource =
  | 'ui'
  | 'midi'
  | 'macro'
  | 'preset'
  | 'randomize'
  | 'automation'
  | 'init';

export type NumericCurve = 'linear' | 'exp';

export interface NumericParameterOptions {
  /** Short human label (e.g. "Cutoff", "Freq"). Shown on knobs. */
  readonly name: string;
  /** Initial value. Must lie within [min, max]. */
  readonly default: number;
  readonly min: number;
  readonly max: number;
  /** 'exp' gives musical response on frequency-like params. */
  readonly curve?: NumericCurve;
  readonly unit?: string;
  /**
   * How aggressively AudioParam writes smooth. 0 = instant, higher = slower
   * settle. Default 20ms is near-zipperless for most knob twists.
   */
  readonly smoothingMs?: number;
  readonly randomizable?: boolean;
  /**
   * Biases the randomizer: <1 skews toward min, >1 toward max. Defaults to 1
   * (uniform). Tuned per-parameter to make random patches usable.
   */
  readonly randomSkew?: number;
  /** Custom display formatter. Default: v.toFixed(2) with optional unit. */
  readonly formatter?: (value: number) => string;
}

export interface EnumParameterOptions<T extends string> {
  readonly name: string;
  readonly default: T;
  readonly options: readonly T[];
  readonly randomizable?: boolean;
  readonly formatter?: (value: T) => string;
}

export type NumericListener = (value: number, source: ParamSource) => void;
export type EnumListener<T extends string> = (value: T, source: ParamSource) => void;

/**
 * NumericParameter — continuous value, optionally bound to one or more
 * AudioParams. Exposes normalize/denormalize for UI controls that want to
 * work in 0..1 space regardless of the underlying range or curve.
 */
export class NumericParameter {
  readonly kind = 'numeric' as const;
  readonly name: string;
  readonly default: number;
  readonly min: number;
  readonly max: number;
  readonly curve: NumericCurve;
  readonly unit: string;
  readonly smoothingMs: number;
  readonly randomizable: boolean;
  readonly randomSkew: number;
  readonly formatter: (value: number) => string;

  private _value: number;
  /**
   * Current audio-rate modulation contribution (summed across connections).
   * Set by the ModRouter's display sampler; not persisted, never pushed to
   * AudioParams (the audio-rate path already adds the source signal there).
   * Lives outside `_value` so the knob can show "base + mod" without
   * double-applying modulation to the underlying AudioParam.
   */
  private _modContribution = 0;
  private readonly listeners = new Set<NumericListener>();
  /**
   * Listeners that fire on display changes — both `.set()` (which changes
   * `_value`) and `setModContribution()` (which changes `_modContribution`).
   * Knobs subscribe here so they visibly track modulation.
   */
  private readonly displayListeners = new Set<NumericListener>();
  private readonly audioParams = new Set<AudioParam>();
  private ctx: AudioContext | null = null;

  constructor(opts: NumericParameterOptions) {
    if (opts.max <= opts.min) {
      throw new Error(`[param:${opts.name}] max must be > min`);
    }
    if (opts.curve === 'exp' && opts.min <= 0) {
      throw new Error(
        `[param:${opts.name}] exp curve requires min > 0 (got ${opts.min})`,
      );
    }
    this.name = opts.name;
    this.default = opts.default;
    this.min = opts.min;
    this.max = opts.max;
    this.curve = opts.curve ?? 'linear';
    this.unit = opts.unit ?? '';
    this.smoothingMs = opts.smoothingMs ?? 20;
    this.randomizable = opts.randomizable ?? true;
    this.randomSkew = opts.randomSkew ?? 1;
    this.formatter = opts.formatter ?? ((v) => v.toFixed(2));
    this._value = this.clamp(opts.default);
  }

  get value(): number {
    return this._value;
  }

  /**
   * Set a new value. No-op if unchanged. Pushes to bound AudioParams with
   * smoothing, then notifies listeners.
   */
  set(value: number, source: ParamSource = 'ui'): void {
    const clamped = this.clamp(value);
    if (clamped === this._value) return;
    this._value = clamped;
    this.pushToAudioParams(clamped);
    for (const fn of this.listeners) fn(clamped, source);
    // Display follows base changes too — user drags, preset loads, macros all
    // update what the knob should show.
    for (const fn of this.displayListeners) fn(this.displayValue, source);
  }

  /**
   * Update the audio-rate modulation contribution reflected by the knob.
   * Called by the ModRouter's display sampler (never by user code). Fires
   * only the display listener channel so macros and other value subscribers
   * don't see this as a new base write.
   */
  setModContribution(contribution: number): void {
    if (contribution === this._modContribution) return;
    this._modContribution = contribution;
    for (const fn of this.displayListeners) fn(this.displayValue, 'automation');
  }

  /**
   * Set via a 0..1 normalized position (for knobs). Respects the curve.
   */
  setNormalized(n: number, source: ParamSource = 'ui'): void {
    this.set(this.fromNormalized(n), source);
  }

  reset(source: ParamSource = 'ui'): void {
    this.set(this.default, source);
  }

  /**
   * Bind to a native AudioParam. Subsequent `.set()` calls push with smoothing.
   * Modules call this once in init(); the Parameter keeps a weak-ish handle.
   */
  bindAudioParam(ap: AudioParam, ctx: AudioContext): void {
    this.ctx = ctx;
    this.audioParams.add(ap);
    ap.setValueAtTime(this._value, ctx.currentTime);
  }

  unbindAudioParam(ap: AudioParam): void {
    this.audioParams.delete(ap);
  }

  subscribe(fn: NumericListener): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  /**
   * Subscribe to display changes — base value AND modulation contribution.
   * Intended for UI (knobs) that should visibly follow modulation without
   * participating in the baseline-tracking logic of value listeners.
   */
  subscribeDisplay(fn: NumericListener): () => void {
    this.displayListeners.add(fn);
    return () => {
      this.displayListeners.delete(fn);
    };
  }

  /** Instantaneous display value: base value plus current mod contribution. */
  get displayValue(): number {
    return this.clamp(this._value + this._modContribution);
  }

  /** Display value in 0..1 normalized space — drop-in replacement for `normalized`. */
  get normalizedDisplay(): number {
    return this.toNormalized(this.displayValue);
  }

  /** Formatted display value — what the knob readout should show. */
  formatDisplay(): string {
    return this.format(this.displayValue);
  }

  /** Map the current (or given) value to 0..1, respecting the curve. */
  toNormalized(v = this._value): number {
    const c = this.clamp(v);
    if (this.curve === 'exp') {
      return Math.log(c / this.min) / Math.log(this.max / this.min);
    }
    return (c - this.min) / (this.max - this.min);
  }

  /** Inverse of toNormalized. Clamps input to 0..1. */
  fromNormalized(n: number): number {
    const u = Math.max(0, Math.min(1, n));
    if (this.curve === 'exp') {
      return this.min * Math.pow(this.max / this.min, u);
    }
    return this.min + u * (this.max - this.min);
  }

  format(v = this._value): string {
    const base = this.formatter(v);
    return this.unit ? `${base} ${this.unit}` : base;
  }

  /** Current normalized position, for UI controls that render a 0..1 dial. */
  get normalized(): number {
    return this.toNormalized();
  }

  private clamp(v: number): number {
    if (Number.isNaN(v)) return this._value ?? this.default;
    return Math.max(this.min, Math.min(this.max, v));
  }

  private pushToAudioParams(v: number): void {
    if (!this.ctx || this.audioParams.size === 0) return;
    const t = this.ctx.currentTime;
    // setTargetAtTime uses an exponential approach; the time-constant is
    // roughly smoothingMs/3 for a 95% settle by smoothingMs. Zero means instant.
    const tc = this.smoothingMs > 0 ? this.smoothingMs / 1000 / 3 : 0;
    for (const ap of this.audioParams) {
      ap.cancelScheduledValues(t);
      if (tc > 0) {
        ap.setTargetAtTime(v, t, tc);
      } else {
        ap.setValueAtTime(v, t);
      }
    }
  }
}

/**
 * EnumParameter<T> — stepped value over a fixed list of string options.
 * Used for waveform selection, filter types, mod routings, etc.
 */
export class EnumParameter<T extends string> {
  readonly kind = 'enum' as const;
  readonly name: string;
  readonly default: T;
  readonly options: readonly T[];
  readonly randomizable: boolean;
  readonly formatter: (value: T) => string;

  private _value: T;
  private readonly listeners = new Set<EnumListener<T>>();

  constructor(opts: EnumParameterOptions<T>) {
    if (!opts.options.includes(opts.default)) {
      throw new Error(
        `[param:${opts.name}] default "${opts.default}" is not in options`,
      );
    }
    this.name = opts.name;
    this.default = opts.default;
    this.options = opts.options;
    this.randomizable = opts.randomizable ?? true;
    this.formatter = opts.formatter ?? ((v) => v);
    this._value = opts.default;
  }

  get value(): T {
    return this._value;
  }

  set(value: T, source: ParamSource = 'ui'): void {
    if (!this.options.includes(value)) return;
    if (value === this._value) return;
    this._value = value;
    for (const fn of this.listeners) fn(value, source);
  }

  setByIndex(index: number, source: ParamSource = 'ui'): void {
    const clamped = Math.max(0, Math.min(this.options.length - 1, Math.floor(index)));
    const next = this.options[clamped];
    if (next !== undefined) this.set(next, source);
  }

  reset(source: ParamSource = 'ui'): void {
    this.set(this.default, source);
  }

  get index(): number {
    return this.options.indexOf(this._value);
  }

  subscribe(fn: EnumListener<T>): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  format(v: T = this._value): string {
    return this.formatter(v);
  }
}

/**
 * Type union for places (UI, presets) that accept either flavor of parameter.
 * Concrete modules typically type their parameters narrowly, but the preset
 * serializer and the generic module-panel UI work over AnyParameter.
 *
 * We use `any` here (not `string`) on purpose: EnumParameter<'a' | 'b'> isn't
 * assignable to EnumParameter<string> because `set(v: 'a' | 'b')` is narrower
 * than `set(v: string)` (contravariant parameter position). Using `any` opts
 * that branch into bivariant assignment so modules can mix narrow enums into
 * the array without lying about their own types.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyParameter = NumericParameter | EnumParameter<any>;
